'use client';

import { useEffect, useState } from 'react';

/**
 * Portal-owned copies of shared-ui's viewport hooks (src/utils, v0.5.383) with
 * identical breakpoints and resize behaviour, so the login/home path needs no
 * shared-ui code. shared-ui reads the window through its WindowProvider
 * context; the portal never mounts one, so that indirection always resolved to
 * the global window — read directly here, guarded for the server render pass.
 */

const DESKTOP_WIDTH = 1024;

export function useIsDesktop(minWidth = DESKTOP_WIDTH): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= minWidth
  );

  useEffect(() => {
    const updateIsDesktop = () => {
      setIsDesktop(window.innerWidth >= minWidth);
    };

    updateIsDesktop();
    window.addEventListener('resize', updateIsDesktop);
    return () => {
      window.removeEventListener('resize', updateIsDesktop);
    };
  }, [minWidth]);

  return isDesktop;
}

export function useIsLandscape(): boolean {
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  useEffect(() => {
    const updateIsLandscape = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsLandscapeMobile(height <= 500 && width > height);
    };

    updateIsLandscape();
    window.addEventListener('resize', updateIsLandscape);
    return () => {
      window.removeEventListener('resize', updateIsLandscape);
    };
  }, []);

  return isLandscapeMobile;
}
